import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  selectTransactionBalance,
  selectTransactionOverdueAmount,
  selectTransactionsList,
  selectTransactionsListCount,
  selectTransactionsQueryOptions
} from 'src/store/selectors/transactionSelectors';
import {
  CLEAR_TRANSACTIONS,
  createManualTransaction,
  loadClientTransaction,
  LOAD_TRANSACTIONS,
  downloadClientTransactionsList
} from 'src/store/actions/transactionActions';
import { loadClientContracts } from 'src/store/actions/clientActions';
import { selectClientContractList } from 'src/store/selectors/clientSelectors';
import { useParams } from 'react-router-dom';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import MenuButton from 'src/components/MenuButton/MenuButton';
import {
  CreateTransactionOptions,
  InvoicePaymentStatusFilter
} from 'src/utils/constants/transactions';
import useDebounce from 'src/hooks/useDebounce';
import useStyles from 'src/components/AccountStatement/styles';
import AccountStatementTable from 'src/components/AccountStatement/AccountStatementTable';
import TransactionsToolbar from 'src/components/AccountStatement/TransactionsToolbar';
import InvoiceDetailDrawer from 'src/components/AccountStatement/InvoiceDetailDrawer';
import GenerateChargeDialog from './GenerateChargeDialog';
import GeneratePaymentDialog from './GenerateManualPaymentDialog';
import GeneratDiscountDialog from './GenerateManualDiscountDialog';

const DialogOptions = {
  DISCOUNT: 'DISCOUNT',
  PAYMENT: 'PAYMENT',
  CHARGE: 'CHARGE'
};

const createTransactionsOptions = [
  {
    label: 'CLIENTS_VIEW.CLIENT_DETAIL.ACCOUNTING_STATEMENT.MANUAL_CHARGE',
    key: CreateTransactionOptions.CREATE_CHARGE,
    icon: <AddIcon />
  },
  {
    label: 'CLIENTS_VIEW.CLIENT_DETAIL.ACCOUNTING_STATEMENT.MANUAL_PAYMENT',
    key: CreateTransactionOptions.CREATE_PAYMENT,
    icon: <AddIcon />
  },
  {
    label: 'TRANSACTION.CATEGORY.DISCOUNT',
    key: CreateTransactionOptions.CREATE_DISCOUNT,
    icon: <AddIcon />
  }
];

const AccountStatementDetail = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const transactionCount = useSelector(selectTransactionsListCount);
  const transactions = useSelector(selectTransactionsList);
  const balance = useSelector(selectTransactionBalance);
  const overdueAmount = useSelector(selectTransactionOverdueAmount);
  const queryOptions = useSelector(selectTransactionsQueryOptions);
  const contracts = useSelector(selectClientContractList);

  const { id } = useParams();
  const loading = useSelector(isLoadingSelector([LOAD_TRANSACTIONS.action]));

  const [open, setOpen] = useState('');
  const [folio, setFolio] = useState('');
  const [contractId, setContractId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(
    InvoicePaymentStatusFilter.ALL
  );
  const [cutoffDate, setCutoffDate] = useState(null);
  const [viewingInvoiceId, setViewingInvoiceId] = useState(null);

  const debounceFilter = useDebounce(
    queryOpts => dispatch(loadClientTransaction(id, { ...queryOpts, page: 0 })),
    400
  );

  const applyFilters = useCallback(
    overrides => {
      debounceFilter({
        ...queryOptions,
        page: 0,
        folio,
        contractId: contractId || undefined,
        paymentStatus:
          paymentStatus === InvoicePaymentStatusFilter.ALL
            ? undefined
            : paymentStatus,
        beforeDate: cutoffDate,
        ...overrides
      });
    },
    [debounceFilter, queryOptions, folio, contractId, paymentStatus, cutoffDate]
  );

  const handleFolioChange = useCallback(
    value => {
      setFolio(value);
      applyFilters({ folio: value });
    },
    [applyFilters]
  );

  const handleContractChange = useCallback(
    value => {
      setContractId(value);
      applyFilters({ contractId: value || undefined });
    },
    [applyFilters]
  );

  const handlePaymentStatusChange = useCallback(
    value => {
      setPaymentStatus(value);
      applyFilters({
        paymentStatus:
          value === InvoicePaymentStatusFilter.ALL ? undefined : value
      });
    },
    [applyFilters]
  );

  const handleApplyCutoffDate = useCallback(
    value => {
      setCutoffDate(value);
      applyFilters({ beforeDate: value });
    },
    [applyFilters]
  );

  useEffect(() => {
    dispatch(loadClientTransaction(id, { page: 0 }));
    dispatch(loadClientContracts({ clientId: id, status: undefined }, false));
    return () => {
      dispatch({ type: CLEAR_TRANSACTIONS });
    };
  }, [dispatch, id]);

  const handleMovementsChangePage = useCallback(
    (_, value) => {
      dispatch(loadClientTransaction(id, { page: value }));
    },
    [dispatch, id]
  );

  const handleViewInvoice = useCallback(invoiceId => {
    setViewingInvoiceId(invoiceId);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setViewingInvoiceId(null);
  }, []);

  const handleDrawerMutated = useCallback(() => {
    dispatch(loadClientTransaction(id, queryOptions));
  }, [dispatch, id, queryOptions]);

  const handleOpenDiscountDialog = useCallback(() => {
    setOpen(DialogOptions.DISCOUNT);
  });

  const handleOpenPaymentDialog = useCallback(() => {
    setOpen(DialogOptions.PAYMENT);
  });

  const handleOpenChargeDialog = useCallback(() => {
    setOpen(DialogOptions.CHARGE);
  });

  const handleCloseDialog = useCallback(() => {
    setOpen('');
  });

  const handleCreateTransactionOptions = value => {
    switch (value) {
      case CreateTransactionOptions.CREATE_CHARGE:
        handleOpenChargeDialog();
        break;
      case CreateTransactionOptions.CREATE_PAYMENT:
        handleOpenPaymentDialog();
        break;
      case CreateTransactionOptions.CREATE_DISCOUNT:
        handleOpenDiscountDialog();
        break;
      default:
        break;
    }
  };

  const handleSubmitTransaction = useCallback((values, handleClose) => {
    dispatch(
      createManualTransaction(
        {
          ...values,
          clientId: Number(id)
        },
        handleClose
      )
    );
  }, []);

  const handleExcelExport = useCallback(() => {
    dispatch(downloadClientTransactionsList(id, queryOptions));
  }, [dispatch, id, queryOptions]);

  return (
    <Box mt={3}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box className={classes.headerRow}>
            <Box>
              <Typography
                style={{ fontWeight: '400', textTransform: 'capitalize' }}
                color="primary"
                variant="h1"
              >
                {t('NAVIGATION.TRANSACTIONS')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('COMMON.AMOUNTS_WITH_IVA')}
              </Typography>
            </Box>
            <MenuButton
              label={t('COMMON.CREATE_TRANSACTION')}
              options={createTransactionsOptions}
              handleClickOption={handleCreateTransactionOptions}
              styles={{
                width: 240,
                flexGrow: 0,
                flexShrink: 0,
                alignSelf: 'flex-start'
              }}
              primary
              endIcon={<KeyboardArrowDownIcon />}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box className={classes.cardsRow}>
            <Box className={classes.transaction}>
              <Box className={classes.transactionIcon}>
                <MoneyIcon fill="#28A745" />
              </Box>
              <Grid item>
                <Typography
                  style={{ marginBottom: 8 }}
                  color="textPrimary"
                  component="p"
                  variant="body2"
                >
                  {t('COMMON.BALANCE')}
                </Typography>
                <Typography color="primary" component="h2" variant="h2">
                  {formatAmountWithDecimals(balance)}
                </Typography>
              </Grid>
            </Box>
            <Box className={classes.transaction}>
              <Box className={classes.overdueIcon}>!</Box>
              <Grid item>
                <Typography
                  style={{ marginBottom: 8 }}
                  color="textPrimary"
                  component="p"
                  variant="body2"
                >
                  {t('TRANSACTION.OVERDUE_AMOUNT')}
                </Typography>
                <Typography color="primary" component="h2" variant="h2">
                  {formatAmountWithDecimals(overdueAmount)}
                </Typography>
              </Grid>
            </Box>
          </Box>
        </Grid>
        <Grid item lg={12}>
          <TransactionsToolbar
            folio={folio}
            onFolioChange={handleFolioChange}
            contracts={contracts}
            contractId={contractId}
            onContractChange={handleContractChange}
            paymentStatus={paymentStatus}
            onPaymentStatusChange={handlePaymentStatusChange}
            cutoffDate={cutoffDate}
            onApplyCutoffDate={handleApplyCutoffDate}
            onDownload={handleExcelExport}
          />
          <AccountStatementTable
            className={classes.table}
            count={transactionCount}
            page={queryOptions.page}
            loading={loading}
            handleChangePage={handleMovementsChangePage}
            transactions={transactions}
            onView={handleViewInvoice}
          />
        </Grid>
      </Grid>
      <GenerateChargeDialog
        open={open === DialogOptions.CHARGE}
        handleClose={handleCloseDialog}
        submit={handleSubmitTransaction}
        clientId={id}
      />
      <GeneratePaymentDialog
        open={open === DialogOptions.PAYMENT}
        handleClose={handleCloseDialog}
        submit={handleSubmitTransaction}
        clientId={id}
      />
      <GeneratDiscountDialog
        open={open === DialogOptions.DISCOUNT}
        handleClose={handleCloseDialog}
        submit={handleSubmitTransaction}
        clientId={id}
      />
      <InvoiceDetailDrawer
        open={Boolean(viewingInvoiceId)}
        invoiceId={viewingInvoiceId}
        cutoffDate={cutoffDate}
        onClose={handleCloseDrawer}
        onMutated={handleDrawerMutated}
      />
    </Box>
  );
};

export default AccountStatementDetail;
